import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Cart buttons are always dynamically managed
    minusButton.disabled = value <= min;
    plusButton.disabled =
      this.serverDisabledPlus ||
      (this.dataset.isTicketProduct !== 'true' && effectiveMax !== null && value >= effectiveMax);

    this.syncUnitsInput();
  }

  /**
   * Shows the cart line error instead of increasing a ticket beyond its limit.
   * @param {Event} event
   */
  increaseQuantity(event) {
    const { max, value } = this.getCurrentValues();
    if (this.dataset.isTicketProduct === 'true' && max !== null && value >= max) {
      event.preventDefault();
      this.#showTicketLimitError();
      return;
    }

    super.increaseQuantity(event);
  }

  #showTicketLimitError() {
    const line = this.refs.quantityInput.dataset.cartLine;
    const errorMessage = this.dataset.ticketLimitError;
    if (!line || !errorMessage) return;

    const errorContainer = document.querySelector(`[ref="cartItemErrorContainer-${line}"]`);
    const errorText = document.querySelector(`[ref="cartItemError-${line}"]`);
    if (!(errorContainer instanceof HTMLElement) || !(errorText instanceof HTMLElement)) return;

    errorText.textContent = errorMessage;
    errorContainer.classList.remove('hidden');
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
